<template>
  <div class="error-scene">
    <div class="error-illustration" :class="[`scene-${code}`, compact ? 'is-compact' : '']">
      <div class="halo halo-left"></div>
      <div class="halo halo-right"></div>
      <div class="ring"></div>
      <div class="spark spark-a"></div>
      <div class="spark spark-b"></div>
      <div class="spark spark-c"></div>

      <div class="city">
        <div class="building building-left"></div>
        <div class="building building-mid-left"></div>
        <div class="building building-main"></div>
        <div class="building building-mid-right"></div>
        <div class="building building-right"></div>
      </div>

      <div class="code-mark">{{ code }}</div>
      <div v-if="badgeText" class="badge">{{ badgeText }}</div>
      <div class="platform"></div>
    </div>

    <p class="description">{{ description }}</p>

    <div class="actions">
      <button class="action-link" type="button" @click="$emit('primary')">
        <i :class="primaryIcon"></i>
        <span>{{ primaryText }}</span>
      </button>
      <button v-if="secondaryText" class="action-link" type="button" @click="$emit('secondary')">
        <i :class="secondaryIcon"></i>
        <span>{{ secondaryText }}</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ErrorScene',
  props: {
    code: {
      type: String,
      default: '404'
    },
    description: {
      type: String,
      default: ''
    },
    primaryText: {
      type: String,
      default: '刷新页面'
    },
    secondaryText: {
      type: String,
      default: ''
    },
    primaryIcon: {
      type: String,
      default: 'el-icon-refresh'
    },
    secondaryIcon: {
      type: String,
      default: 'el-icon-house'
    },
    badgeText: {
      type: String,
      default: ''
    },
    compact: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style scoped>
.error-scene {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.error-illustration {
  position: relative;
  width: min(420px, 86vw);
  height: 250px;
  margin-bottom: 18px;
  animation: float-in 0.75s ease-out;
}

.error-illustration.is-compact {
  width: min(380px, 84vw);
}

.halo {
  position: absolute;
  border-radius: 50%;
  filter: blur(20px);
  opacity: 0.6;
}

.halo-left {
  width: 120px;
  height: 120px;
  left: 66px;
  top: 68px;
  background: rgba(201, 214, 255, 0.45);
}

.halo-right {
  width: 150px;
  height: 150px;
  right: 70px;
  top: 92px;
  background: rgba(219, 227, 255, 0.58);
}

.ring {
  position: absolute;
  left: 94px;
  top: 32px;
  width: 98px;
  height: 42px;
  border: 10px solid rgba(165, 189, 245, 0.7);
  border-radius: 50%;
  transform: rotate(-28deg);
}

.ring::after {
  content: '';
  position: absolute;
  inset: 9px 20px auto auto;
  width: 28px;
  height: 12px;
  border-radius: 50%;
  background: rgba(232, 239, 255, 0.95);
}

.spark {
  position: absolute;
  width: 14px;
  height: 14px;
  transform: rotate(45deg);
  background: rgba(162, 183, 246, 0.8);
}

.spark::before,
.spark::after {
  content: '';
  position: absolute;
  inset: 0;
  background: inherit;
}

.spark::before {
  transform: scaleX(0.38);
}

.spark::after {
  transform: scaleY(0.38);
}

.spark-a {
  left: 128px;
  bottom: 54px;
}

.spark-b {
  right: 116px;
  top: 56px;
}

.spark-c {
  right: 84px;
  bottom: 84px;
  opacity: 0.55;
}

.city {
  position: absolute;
  left: 50%;
  bottom: 50px;
  transform: translateX(-50%);
  width: 248px;
  height: 156px;
}

.building {
  position: absolute;
  bottom: 0;
  border-radius: 3px 3px 0 0;
  overflow: hidden;
}

.building::before {
  content: '';
  position: absolute;
  inset: 10px 9px auto;
  height: calc(100% - 22px);
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.72) 0, rgba(255, 255, 255, 0.72) 18px, transparent 18px, transparent 24px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.72) 0, rgba(255, 255, 255, 0.72) 8px, transparent 8px, transparent 16px);
  background-size: 24px 28px;
  opacity: 0.7;
}

.building-left,
.building-mid-left,
.building-mid-right,
.building-right {
  background: linear-gradient(180deg, rgba(184, 199, 241, 0.7), rgba(166, 183, 229, 0.44));
}

.building-left {
  left: 0;
  width: 32px;
  height: 126px;
}

.building-mid-left {
  left: 38px;
  width: 48px;
  height: 144px;
}

.building-main {
  left: 78px;
  width: 74px;
  height: 178px;
  background: linear-gradient(180deg, rgba(255, 247, 224, 0.96), rgba(242, 235, 214, 0.58));
  box-shadow: 0 0 32px rgba(255, 243, 196, 0.7);
}

.building-main::before {
  inset: 14px 14px auto;
  background-image:
    linear-gradient(rgba(255, 247, 225, 0.92) 0, rgba(255, 247, 225, 0.92) 18px, transparent 18px, transparent 26px),
    linear-gradient(90deg, rgba(255, 247, 225, 0.95) 0, rgba(255, 247, 225, 0.95) 8px, transparent 8px, transparent 18px);
  background-size: 26px 30px;
  opacity: 0.95;
}

.building-mid-right {
  left: 140px;
  width: 38px;
  height: 118px;
}

.building-right {
  right: 0;
  width: 48px;
  height: 158px;
}

.code-mark {
  position: absolute;
  left: 50%;
  top: 118px;
  transform: translateX(-50%);
  font-size: clamp(72px, 11vw, 88px);
  line-height: 1;
  font-weight: 700;
  letter-spacing: 2px;
  color: rgba(112, 149, 231, 0.7);
  text-shadow: 0 8px 24px rgba(169, 191, 247, 0.25);
}

.scene-403 .code-mark {
  color: rgba(108, 137, 217, 0.76);
}

.badge {
  position: absolute;
  left: 50%;
  top: 106px;
  transform: translateX(78px);
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #7e9fff, #5f82ea);
  box-shadow: 0 10px 18px rgba(106, 136, 226, 0.28);
}

.platform {
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  width: 98px;
  height: 16px;
  border-radius: 50%;
  background: rgba(173, 190, 232, 0.38);
}

.description {
  margin: 0;
  color: #2f3442;
  font-size: 18px;
  line-height: 1.8;
  text-align: center;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  margin-top: 22px;
  flex-wrap: wrap;
}

.action-link {
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #4e7bff;
  font-size: 16px;
  cursor: pointer;
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.action-link:hover {
  opacity: 0.86;
  transform: translateY(-1px);
}

.action-link i {
  font-size: 18px;
}

@keyframes float-in {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 640px) {
  .error-illustration {
    height: 214px;
  }

  .city {
    width: 220px;
    height: 142px;
  }

  .building-main {
    height: 156px;
  }

  .building-right {
    height: 138px;
  }

  .code-mark {
    top: 112px;
  }

  .description {
    font-size: 16px;
    line-height: 1.7;
  }

  .actions {
    gap: 18px;
  }
}
</style>
